import { FormattedMessage, useIntl } from 'react-intl'
import { useInView } from "react-intersection-observer";

export const WhereCanIPay = () => {
  const intl = useIntl();
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const paymentMethods = [
    {
      icon: (
        <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      ),
      name: "Transferencia",
      desc: "Desde cualquier banco"
    },
    {
      icon: (
        <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      name: "Depósito",
      desc: "En agencia bancaria"
    },
    {
      icon: (
        <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      ),
      name: "Pago con link",
      desc: "Rápido y seguro"
    },
  ];

  return (
    <section className="section relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-surface-50 to-white" />
      <div className="absolute top-0 left-1/2 w-96 h-96 bg-accent/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />

      <div className="container-main relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div
            ref={ref}
            className={`
              relative order-2 lg:order-1
              transform transition-all duration-1000 ease-out
              ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}
            `}
          >
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-accent/10 rounded-3xl blur-3xl scale-90" />

              {/* Phone mockup container */}
              <div className="relative bg-gradient-to-br from-primary to-brand-900 rounded-3xl p-6 md:p-8">
                <img
                  src={`${process.env.PUBLIC_URL}/assets/request_credit.png`}
                  alt="Solicitar crédito en Efektivos"
                  className="w-full max-w-sm mx-auto drop-shadow-2xl"
                />
              </div>

              {/* Floating badge */}
              <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-elevated p-4 border border-surface-100 hidden md:block">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-body-sm font-bold text-primary">Pago fácil</div>
                    <div className="text-caption text-surface-500">Múltiples opciones</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="order-1 lg:order-2">
            <span className="badge mb-4">Pagos</span>
            <h2 className="text-display-md md:text-display-lg text-primary mb-4">
              <FormattedMessage id="APP.WHERE.CAN.I.PAY.SECTION.TITLE" />
            </h2>
            <p className="text-body-lg text-surface-600 mb-6">
              <FormattedMessage id="APP.WHERE.CAN.I.PAY.SECTION.DESCRIPTION.2" />
            </p>
            <p
              dangerouslySetInnerHTML={{ __html: intl.formatMessage({ id: "APP.WHERE.CAN.I.PAY.SECTION.DESCRIPTION.1" }) }}
              className="text-body-md text-surface-600 mb-8"
            />

            {/* Payment methods */}
            <div className="space-y-4">
              {paymentMethods.map((method, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-4 bg-white rounded-xl border border-surface-200 hover:border-accent/40 hover:shadow-card transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center">
                    {method.icon}
                  </div>
                  <div>
                    <div className="text-body-md font-semibold text-primary">{method.name}</div>
                    <div className="text-body-sm text-surface-500">{method.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Important note */}
            <div className="mt-6 p-4 bg-accent/5 border border-accent/20 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <div className="text-body-sm font-semibold text-primary mb-1">Importante</div>
                  <p className="text-body-sm text-surface-600">
                    Recuerda reportar tu pago en la app para que se refleje en tu cuenta de forma inmediata.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
